import { z } from 'zod';
import { moneySchema } from "./event-schemas.js";
/**
 * The public event projection.
 *
 * This is a deliberate contract, not a serialization of the organizer's event.
 * Everything an attendee is shown is listed here explicitly, and the projection
 * service maps field by field — so adding an operational field to the event
 * aggregate cannot leak it to the public by default.
 *
 * **Deliberately not public** (SDAD Section 40, Data Class C):
 *
 *   - gates, gate policies, admission windows, re-entry rules, identity-check
 *     flags — how a venue is secured is not something an attacker should be able
 *     to read off a marketing page;
 *   - access zones and which zone a ticket maps to — an attendee is sold a
 *     product the organizer named, not an operational zone identifier;
 *   - capacity, allocations, holdbacks and how many remain — SDAD Section 40
 *     lists public ticket product *names and prices* as Class A, and says
 *     nothing about inventory. Remaining-count is competitive intelligence and
 *     a pressure tactic, and stays private until there is a deliberate decision
 *     to publish it;
 *   - internal notes, venue spaces, organizer-private profile data;
 *   - anything at all about an event that is not `PUBLISHED`.
 */
export const publicSessionSchema = z.object({
    sessionId: z.string().uuid(),
    name: z.string().nullable(),
    startsAt: z.string(),
    endsAt: z.string(),
    doorsOpenAt: z.string().nullable(),
});
/**
 * A ticket card as an attendee sees it.
 *
 * No capacity, no zone, no security mode. Security mode is a commercial term the
 * buyer must be told about *at checkout*, where it changes what they need to
 * bring; on a discovery card it is noise that also tells a fraudster which
 * events are worth attacking.
 */
export const publicTicketProductSchema = z.object({
    ticketProductId: z.string().uuid(),
    displayName: z.string(),
    description: z.string().nullable(),
    price: moneySchema,
    accessScope: z.enum(['ALL_SESSIONS', 'SPECIFIC_SESSIONS']),
    sessionIds: z.array(z.string().uuid()),
    /**
     * Whether this can be bought right now.
     *
     * `NOT_YET_ON_SALE` is the only honest value in this release: checkout does
     * not exist, so nothing is purchasable and the surface must not pretend
     * otherwise (kickoff Section 17). Slice 3 adds the states that mean something
     * about inventory.
     */
    saleState: z.enum(['NOT_YET_ON_SALE', 'ON_SALE', 'SOLD_OUT', 'CLOSED']),
});
export const publicEventSummarySchema = z.object({
    slug: z.string(),
    title: z.string(),
    publicSummary: z.string().nullable(),
    timezone: z.string(),
    /** The first scheduled session, so a listing can show "when". */
    startsAt: z.string().nullable(),
    sessionCount: z.number().int(),
    venue: z
        .object({
        name: z.string(),
        locality: z.string(),
        countryCode: z.string(),
    })
        .nullable(),
    priceFrom: moneySchema.nullable(),
});
export const publicEventSchema = z.object({
    slug: z.string(),
    title: z.string(),
    publicSummary: z.string().nullable(),
    publicDescription: z.string().nullable(),
    timezone: z.string(),
    publishedAt: z.string(),
    venue: z
        .object({
        name: z.string(),
        locality: z.string(),
        addressLine: z.string().nullable(),
        countryCode: z.string(),
    })
        .nullable(),
    sessions: z.array(publicSessionSchema),
    ticketProducts: z.array(publicTicketProductSchema),
    /**
     * What an attendee can do with this event right now, stated by the server
     * rather than inferred by each client.
     */
    commerce: z.object({
        state: z.enum(['NOT_YET_AVAILABLE', 'AVAILABLE']),
        message: z.string(),
    }),
});
export const publicEventListSchema = z.object({
    /**
     * `AVAILABLE` once discovery itself works, independent of whether any event
     * happens to be listed. An empty `AVAILABLE` list means "nothing on sale";
     * `NOT_YET_AVAILABLE` meant "this feature does not exist". Slice 2 makes
     * discovery real, so the state is now `AVAILABLE` even when the list is empty.
     */
    state: z.literal('AVAILABLE'),
    events: z.array(publicEventSummarySchema),
});
export const publicEventQuerySchema = z.object({
    limit: z.coerce.number().int().min(1).max(60).default(24),
    /** Free-text match over public title and summary only. */
    q: z.string().trim().max(120).optional(),
});
//# sourceMappingURL=public-schemas.js.map